import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { Message, MessageReaction, ProductAction, ProductCard } from '../../types';
import { MESSAGE_TYPES } from '../../constants';
import { hasMarkdown } from '../../utils/markdownDetection';
import { useChatConfig } from '../../context/ChatContext';
import { isHiddenAction } from '../../utils/actionFilters';
import { useMerchantPresetContext } from '../../merchants/MerchantPresetContext';
import MessageActions, { FeedbackCommentInput } from './MessageActions';
import AttachmentPreview from '../InputArea/AttachmentPreview';
import ButtonsMessage from '../rich/ButtonsMessage';
import {
  CardMessage,
  FormMessage,
  OrderCardsMessage,
  ProductCardsMessage,
  SourceCitation,
  ThinkingDisplay,
  ToolCallDisplay,
} from '../rich/lazyRich';
import type { WidgetStrings } from '../../utils/i18n';
import { DEFAULT_WIDGET_STRINGS, formatMessageTime } from '../../utils/i18n';

const MarkdownContent = React.lazy(() => import('./MarkdownContent'));

interface OrderCardAction {
  label: string;
  value?: string;
  href?: string;
  target?: '_self' | '_blank';
}

interface MessageBubbleProps {
  message: Message;
  isMobile?: boolean;
  onButtonClick?: (value: string) => void;
  onProductAction?: (payload: { action: ProductAction; product: ProductCard }) => void;
  onOrderAction?: (payload: {
    action: OrderCardAction;
    orderId?: string | null;
    orderName?: string | null;
  }) => void;
  onFormSubmit?: (formId: string | undefined, data: Record<string, string>) => void;
  /** Resend a message whose `status` is `'failed'`; enables the Retry button. */
  onRetryMessage?: (messageId: string) => void;
  retryDisabled?: boolean;
  /**
   * Thumbs up/down feedback. The bubble is controlled by `message.reaction`; `null`
   * clears, and a thumbs-down may carry an optional short comment.
   */
  onReaction?: (messageId: string, reaction: MessageReaction, comment?: string) => void;
  strings?: WidgetStrings;
  direction?: 'ltr' | 'rtl';
}

const MessageBubble = React.memo(function MessageBubble({
  message,
  onButtonClick,
  onProductAction,
  onOrderAction,
  onFormSubmit,
  onRetryMessage,
  retryDisabled = false,
  onReaction,
  strings = DEFAULT_WIDGET_STRINGS,
  direction = 'ltr',
}: MessageBubbleProps) {
  const {
    enableToolVisualization,
    enableSourceCitations,
    enableThinking,
    showTimestamps,
    trackAnalytics,
  } = useChatConfig();
  const showMessageActions = useMerchantPresetContext().messageActions !== false;
  const isUser = message.sender === 'user';
  const isFailed = isUser && message.status === 'failed';
  const isInterrupted = !isUser && message.interrupted === true;
  const reaction = message.reaction ?? null;
  const [showActions, setShowActions] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<{ id: number; copied: boolean } | null>(null);
  const copyAttemptRef = useRef({ sequence: 0 });
  // Comment affordance shown after a thumbs-down in this mount (not for restored reactions).
  const [commentOpen, setCommentOpen] = useState(false);
  // Announce "thanks" only for reactions the visitor gave here, never for restored ones.
  const [reactedHere, setReactedHere] = useState(false);
  const thumbsDownRef = useRef<HTMLButtonElement>(null);

  const filteredSuggestions = (message.suggestions || []).filter(
    suggestion => !isHiddenAction(suggestion)
  );
  const isCommerceCards =
    !isUser &&
    (message.type === MESSAGE_TYPES.PRODUCT_CARDS || message.type === MESSAGE_TYPES.ORDER_CARDS);
  const isProductCardsMessage = !isUser && message.type === MESSAGE_TYPES.PRODUCT_CARDS;
  const filteredButtons =
    message.type === MESSAGE_TYPES.BUTTONS && message.richContent?.buttons
      ? message.richContent.buttons.filter(
          button => !isHiddenAction(button.label) && !isHiddenAction(button.value)
        )
      : message.richContent?.buttons;
  const productCardButtons = isProductCardsMessage
    ? (message.richContent?.buttons || []).filter(
        button => !isHiddenAction(button.label) && !isHiddenAction(button.value)
      )
    : [];

  const handleReaction = useCallback(
    (next: MessageReaction) => {
      onReaction?.(message.id, next, undefined);
      setReactedHere(next !== null);
      setCommentOpen(next === 'down');
    },
    [message.id, onReaction]
  );

  const closeComment = useCallback(() => {
    setCommentOpen(false);
    thumbsDownRef.current?.focus();
  }, []);

  const handleCommentSubmit = useCallback(
    (comment: string) => {
      onReaction?.(message.id, 'down', comment);
      closeComment();
    },
    [closeComment, message.id, onReaction]
  );

  // A failed send reverts the reaction from outside; drop the comment box with it.
  useEffect(() => {
    if (reaction !== 'down') setCommentOpen(false);
  }, [reaction]);

  useEffect(() => {
    const attempts = copyAttemptRef.current;
    setCopyFeedback(null);
    return () => {
      attempts.sequence++;
    };
  }, [message.id, message.text]);

  const handleCopy = useCallback(async () => {
    const text = message.text || '';
    if (!text) return;
    const attempts = copyAttemptRef.current;
    const attempt = ++attempts.sequence;
    setCopyFeedback(null);
    const showCopyResult = (copied: boolean) => {
      if (attempt === attempts.sequence) setCopyFeedback({ id: attempt, copied });
    };
    const trackCopy = (action: string, data: Record<string, unknown>) => {
      try {
        trackAnalytics(action, data);
      } catch {
        // Host analytics callbacks must not change the clipboard outcome.
      }
    };
    const trackFailure = (reason: string) => {
      showCopyResult(false);
      trackCopy('message_copy_failed', { messageId: message.id, reason });
    };
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        showCopyResult(true);
        trackCopy('message_copied', { messageId: message.id });
        return;
      }
      if (typeof document === 'undefined') {
        trackFailure('clipboard_unavailable');
        return;
      }
      const activeElement = document.activeElement as HTMLElement | null;
      const textarea = document.createElement('textarea');
      try {
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = typeof document.execCommand === 'function' && document.execCommand('copy');
        if (ok) {
          showCopyResult(true);
          trackCopy('message_copied', { messageId: message.id, fallback: true });
        } else {
          trackFailure('clipboard_unavailable');
        }
      } finally {
        textarea.remove();
        activeElement?.focus?.({ preventScroll: true });
      }
    } catch {
      trackFailure('clipboard_error');
    }
  }, [message.text, message.id, trackAnalytics]);

  const alignRight = direction === 'rtl' ? !isUser : isUser;
  const ariaLabel = isUser
    ? strings.yourMessageAria
    : message.authorName || strings.assistantMessageAria;
  const bubbleTailClass = alignRight ? 'bubble-right' : 'bubble-left';
  const bubbleLayoutClass = isCommerceCards
    ? 'w-full max-w-[92%] px-3 py-3'
    : 'max-w-[80%] px-4 py-2';
  const messageText = message.text ? (
    hasMarkdown(message.text) ? (
      <React.Suspense
        fallback={
          <p className="text-sm" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {message.text}
          </p>
        }
      >
        <MarkdownContent text={message.text} />
      </React.Suspense>
    ) : (
      <p className="text-sm" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
        {message.text}
      </p>
    )
  ) : null;
  const attachmentPreviews =
    message.attachments?.map(att => <AttachmentPreview key={att.id} attachment={att} />) || [];
  const responseBody =
    messageText || attachmentPreviews.length > 0 ? (
      <div className={isProductCardsMessage ? 'mt-3' : ''} data-testid="message-response-body">
        {messageText}
        {attachmentPreviews}
      </div>
    ) : null;
  const productCards =
    message.type === MESSAGE_TYPES.PRODUCT_CARDS && message.richContent ? (
      <React.Suspense fallback={null}>
        <ProductCardsMessage
          content={{ ...message.richContent, buttons: undefined }}
          onAction={onProductAction}
          onButtonClick={onButtonClick}
          trackAnalytics={trackAnalytics}
          strings={strings}
        />
      </React.Suspense>
    ) : null;
  const orderCards =
    message.type === MESSAGE_TYPES.ORDER_CARDS && message.richContent ? (
      <React.Suspense fallback={null}>
        <OrderCardsMessage
          content={message.richContent}
          onButtonClick={onButtonClick}
          onAction={onOrderAction}
          strings={strings}
        />
      </React.Suspense>
    ) : null;

  const Bubble = (
    <div
      className={`bubble rw-bubble ${bubbleTailClass} ${bubbleLayoutClass} ${isUser ? 'rw-bubble-user' : 'rw-bubble-agent'} ${
        isFailed ? 'rw-bubble-failed' : ''
      } rounded-2xl border ${
        isUser
          ? `bg-gray-100 text-gray-900 ${isFailed ? 'border-red-300' : 'border-gray-200'}`
          : 'bg-white border-gray-200'
      } relative group`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={e => {
        if (typeof document !== 'undefined' && e.currentTarget.contains(document.activeElement))
          return;
        setShowActions(false);
      }}
      onFocus={() => setShowActions(true)}
      onBlur={e => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setShowActions(false);
      }}
    >
      {!isUser && message.authorName && (
        <div className="mb-1 text-xs font-semibold">{message.authorName}</div>
      )}
      {!isProductCardsMessage && responseBody}

      {message.toolCalls && message.toolCalls.length > 0 && enableToolVisualization && (
        <div className="mt-3 space-y-2">
          <React.Suspense fallback={null}>
            {message.toolCalls.map(tool => (
              <ToolCallDisplay key={tool.id} toolCall={tool} />
            ))}
          </React.Suspense>
        </div>
      )}

      {message.sources && message.sources.length > 0 && enableSourceCitations && (
        <div className="mt-3 space-y-1">
          <div className="text-xs font-semibold text-gray-600 mb-1">{strings.sourcesHeading}</div>
          <React.Suspense fallback={null}>
            {message.sources.map((source, idx) => (
              <SourceCitation
                key={source.id || idx}
                source={source}
                index={idx + 1}
                strings={strings}
              />
            ))}
          </React.Suspense>
        </div>
      )}

      {message.thinking && enableThinking && (
        <React.Suspense fallback={null}>
          <ThinkingDisplay thinking={message.thinking} strings={strings} />
        </React.Suspense>
      )}

      {isInterrupted && (
        <div className="rw-stopped-caption mt-1 text-[11px] text-gray-500 italic">
          {strings.responseStopped}
        </div>
      )}

      {isFailed && (
        <div
          className={`rw-bubble-failed-footer mt-1 flex items-center gap-2 text-[11px] text-red-600 ${
            alignRight ? 'justify-end' : ''
          }`}
        >
          <span role="alert">{message.error || strings.sendFailed}</span>
          {onRetryMessage && !message.meta?.retryRequiresAttachments && (
            <button
              type="button"
              aria-label={message.meta?.outcomeUnknown ? strings.recoverReply : strings.retrySend}
              onClick={() => onRetryMessage(message.id)}
              disabled={retryDisabled}
              className="rw-retry-button font-medium underline underline-offset-2 hover:opacity-80 disabled:opacity-50 focus:outline-2 focus:outline-blue-500"
            >
              {message.meta?.outcomeUnknown ? strings.recoverReply : strings.retry}
            </button>
          )}
        </div>
      )}

      {!isUser && showMessageActions && commentOpen && (
        <FeedbackCommentInput
          onSubmit={handleCommentSubmit}
          onDismiss={closeComment}
          strings={strings}
        />
      )}
      {!isUser && showMessageActions && reactedHere && reaction !== null && (
        <span className="sr-only">{strings.feedbackThanks}</span>
      )}

      {showTimestamps && message.timestamp && (
        <div
          className="mt-1 text-[11px] text-gray-600"
          style={alignRight ? { textAlign: 'end' } : undefined}
        >
          {formatMessageTime(message.timestamp, strings.locale)}
        </div>
      )}

      {message.type === MESSAGE_TYPES.CARD && message.richContent && (
        <React.Suspense fallback={null}>
          <CardMessage content={message.richContent} strings={strings} />
        </React.Suspense>
      )}
      {message.type === MESSAGE_TYPES.BUTTONS && message.richContent && (
        <ButtonsMessage
          content={{ ...message.richContent, buttons: filteredButtons }}
          onButtonClick={onButtonClick}
          trackAnalytics={trackAnalytics}
        />
      )}
      {message.type === MESSAGE_TYPES.FORM && message.richContent && (
        <React.Suspense fallback={null}>
          <FormMessage
            content={{
              ...message.richContent,
              onSubmit: data => {
                if (onFormSubmit) {
                  onFormSubmit(message.richContent?.formId, data);
                  return;
                }
                message.richContent?.onSubmit?.(data);
              },
            }}
            strings={strings}
          />
        </React.Suspense>
      )}
      {productCards}
      {isProductCardsMessage && responseBody}
      {isProductCardsMessage && productCardButtons.length > 0 && (
        <ButtonsMessage
          content={{ buttonStyle: message.richContent?.buttonStyle, buttons: productCardButtons }}
          onButtonClick={onButtonClick}
          trackAnalytics={trackAnalytics}
        />
      )}
      {orderCards}

      {!isUser && filteredSuggestions.length > 0 && (
        <div className="mt-2">
          <ButtonsMessage
            content={{
              buttons: filteredSuggestions.map(suggestion => ({
                label: suggestion,
                value: suggestion,
              })),
            }}
            onButtonClick={onButtonClick}
            trackAnalytics={trackAnalytics}
          />
        </div>
      )}
      {!isUser && showMessageActions && (
        <div aria-live="off">
          <div role="status" aria-live="polite" aria-atomic="true" className="rw-copy-status">
            {copyFeedback && (
              <span key={copyFeedback.id}>
                {copyFeedback.copied ? strings.messageCopied : strings.messageCopyFailed}
              </span>
            )}
          </div>
        </div>
      )}
      {!isUser && showMessageActions && (
        <MessageActions
          reaction={reaction}
          onCopy={handleCopy}
          copyDisabled={!message.text}
          onReaction={handleReaction}
          strings={strings}
          visible={showActions}
          thumbsDownRef={thumbsDownRef}
        />
      )}
    </div>
  );

  return (
    <div
      className={`flex items-end gap-2 w-full ${alignRight ? 'justify-end' : ''} mb-3 animate-fadeIn`}
      // The name belongs on the article: aria-label on the plain bubble div is not exposed.
      role="article"
      aria-label={ariaLabel}
    >
      {Bubble}
    </div>
  );
});

export default MessageBubble;
